import { Button, Col, Empty, Row, Space, Tag, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, FC, ReactNode } from 'react';

import type { PanelBlockProps } from './types';
import { BaseCatalogueCard } from '../system/BaseComponents';
import BannerSlots from '@eshop/pages/catalogue/BannerSlots';
import {
  columnsToSpans,
  SIZE_TO_COLUMNS,
  type SpansByBp,
} from '@eshop/pages/catalogue/gridSize.ts';
import { MediaImage, renderFilterChips, renderSortSelect, toGutterPair } from '../visual/shared';
import { useCategoriesMenuDrawer } from '@eshop/pages/categories/CategoriesMenu';
import { useCatalogueTemplate } from '../TemplateContext';
import type {
  PanelHeroCta,
  PanelHeroMediaSource,
  TemplateResponsiveRatio,
} from '../system/templateTypes';
import { FilterOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

type HeroResponsiveRatio = TemplateResponsiveRatio;

interface HeroBlockSlotProps {
  showTitle?: boolean;
  ratio?: HeroResponsiveRatio;
  media?: PanelHeroMediaSource;
  cta?: PanelHeroCta;
}

const parseAspectRatio = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  const colonMatch = normalized.match(/^(\d+)\s*:\s*(\d+)$/);
  if (colonMatch) {
    const width = Number(colonMatch[1]);
    const height = Number(colonMatch[2]);
    if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
      return `${width} / ${height}`;
    }
  }

  const legacyMatch = normalized.match(/aspect_(\d+)-(\d+)/i);
  if (legacyMatch) {
    const width = Number(legacyMatch[1]);
    const height = Number(legacyMatch[2]);
    if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
      return `${width} / ${height}`;
    }
  }

  return null;
};

const resolveResponsiveRatio = (
  ratio: HeroResponsiveRatio | undefined,
  isMobile: boolean,
): string | null => {
  if (!ratio) {
    return null;
  }

  if (typeof ratio === 'string') {
    return parseAspectRatio(ratio);
  }

  const orderedCandidates = isMobile
    ? [ratio.mobile, ratio.default, ratio.tablet, ratio.desktop]
    : [ratio.desktop, ratio.default, ratio.tablet, ratio.mobile];

  for (const candidate of orderedCandidates) {
    const parsed = parseAspectRatio(candidate);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

const useMediaQuery = (query: string): boolean => {
  const getMatches = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState<boolean>(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQueryList = window.matchMedia(query);

    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', listener);

    return () => {
      mediaQueryList.removeEventListener('change', listener);
    };
  }, [query]);

  return matches;
};

const buildHeroCta = (cta: PanelHeroCta | undefined): ReactNode => {
  if (!cta?.label) {
    return null;
  }

  const sharedProps = {
    type: 'primary' as const,
    size: 'large' as const,
    className: 'catalogue-hero__cta',
    'aria-label': cta.ariaLabel ?? cta.label,
  };

  if (cta.href) {
    return (
      <Button
        {...sharedProps}
        href={cta.href}
        target={cta.target}
        rel={cta.rel}
        icon={cta.icon}
      >
        {cta.label}
      </Button>
    );
  }

  return (
    <Button {...sharedProps} onClick={cta.onClick} icon={cta.icon} disabled={!cta.onClick}>
      {cta.label}
    </Button>
  );
};

const buildHeroMediaNode = (
  media: PanelHeroMediaSource | undefined,
  aspectRatio: string | null,
  fallbackAlt: string,
): ReactNode => {
  if (!media) {
    return null;
  }

  const ratioFromMedia =
    typeof media.ratio === 'string'
      ? parseAspectRatio(media.ratio)
      : parseAspectRatio(media.ratio?.default ?? media.ratio?.desktop ?? media.ratio?.mobile);
  const resolvedAspect = aspectRatio ?? ratioFromMedia ?? undefined;
  const containerStyle: CSSProperties = {
    width: '100%',
    borderRadius: 'var(--catalogue-hero-radius, 24px)',
    overflow: 'hidden',
    boxShadow: 'var(--catalogue-hero-shadow, none)',
  };

  if (resolvedAspect) {
    containerStyle.aspectRatio = resolvedAspect;
  }

  if (media.type === 'video') {
    const { sources = [], src, poster, autoPlay, loop, muted, controls, playsInline } = media;

    return (
      <div className="catalogue-hero__media" style={containerStyle}>
        <video
          className="catalogue-hero__video"
          poster={poster}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted ?? autoPlay ?? false}
          controls={controls}
          playsInline={playsInline ?? true}
        >
          {sources.map((source, index) => (
            <source
              key={`${source.src ?? source.srcSet ?? index}`}
              src={source.src}
              srcSet={source.srcSet}
              media={source.media}
              type={source.type}
            />
          ))}
          {src ? <source src={src} /> : null}
        </video>
      </div>
    );
  }

  return (
    <MediaImage
      className="catalogue-hero__media"
      imageClassName="catalogue-hero__media-image"
      aspect={resolvedAspect ?? undefined}
      src={media.src}
      srcSet={media.srcSet}
      sizes={media.sizes}
      alt={media.alt ?? fallbackAlt}
      loading={media.loading ?? 'eager'}
      decoding={media.decoding}
      fetchPriority={media.fetchPriority ?? 'high'}
      objectFit="cover"
      variant="hero"
    />
  );
};

interface PanelDesignConfig {
  toolbarClassName?: string;
  chipContainerClassName?: string;
  chipClassName?: string;
  clearButtonClassName?: string;
  sortClassName?: string;
  sortPlaceholder?: string;
  eyebrowClassName: string;
  resultTagClassName: string;
  loadMoreButtonType: 'default' | 'primary';
  loadMoreButtonClassName?: string;
  defaultEmptyDescription: string;
}

const PANEL_DESIGN_CONFIG: Record<string, PanelDesignConfig> = {
  minimalista: {
    toolbarClassName: 'catalogue-panel__toolbar catalogue-panel__toolbar--minimalista',
    chipContainerClassName: 'catalogue-toolbar__chips catalogue-toolbar__chips--minimalista',
    chipClassName: 'catalogue-chip catalogue-chip--minimalista',
    clearButtonClassName: 'catalogue-toolbar__clear catalogue-toolbar__clear--minimalista',
    sortClassName: 'catalogue-sort catalogue-sort--minimalista',
    sortPlaceholder: 'Ordenar por',
    eyebrowClassName: 'catalogue-panel__eyebrow text-uppercase d-block',
    resultTagClassName: 'catalogue-panel__result-tag',
    loadMoreButtonType: 'default',
    loadMoreButtonClassName: 'catalogue-panel__load-more-btn catalogue-panel__load-more-btn--minimalista',
    defaultEmptyDescription: 'Ajusta los filtros para continuar explorando.',
  },
  moderno: {
    toolbarClassName: 'catalogue-panel__toolbar',
    chipContainerClassName: 'catalogue-toolbar__chips',
    chipClassName: 'catalogue-chip',
    clearButtonClassName: 'catalogue-toolbar__clear',
    sortClassName: 'catalogue-sort catalogue-sort--moderno',
    sortPlaceholder: 'Ordenar',
    eyebrowClassName: 'catalogue-panel__eyebrow text-uppercase fw-semibold d-block',
    resultTagClassName: 'catalogue-panel__result-tag',
    loadMoreButtonType: 'primary',
    defaultEmptyDescription: 'Ajusta los filtros para descubrir más productos.',
  },
};

const getPanelConfig = (design: string | undefined): PanelDesignConfig => {
  const base = PANEL_DESIGN_CONFIG[design ?? ''];
  return (
    base ?? {
      toolbarClassName: 'catalogue-panel__toolbar',
      chipContainerClassName: 'catalogue-toolbar__chips',
      chipClassName: 'catalogue-chip',
      clearButtonClassName: 'catalogue-toolbar__clear',
      sortClassName: 'catalogue-sort',
      sortPlaceholder: 'Ordenar',
      eyebrowClassName: 'catalogue-panel__eyebrow text-uppercase d-block',
      resultTagClassName: 'catalogue-panel__result-tag',
      loadMoreButtonType: 'primary',
      defaultEmptyDescription: 'Ajusta los filtros para descubrir más productos.',
    }
  );
};

const renderToolbarChips = (
  filters: PanelBlockProps['filters'],
  config: PanelDesignConfig,
): ReactNode => {
  if (!filters?.chips?.length) {
    return null;
  }

  return renderFilterChips(filters.chips, {
    containerClassName: config.chipContainerClassName,
    chipClassName: config.chipClassName,
  });
};

export const HeroBlock: FC<PanelBlockProps & HeroBlockSlotProps> = ({
  hero,
  status,
  design,
  ratio: _ratioOverride = undefined,
  showTitle = true,
  media: mediaOverride,
  cta: ctaOverride,
}) => {
  const config = getPanelConfig(design);
  const heroRef = useRef<HTMLElement | null>(null);
  const initialAspectRef = useRef<string | null | undefined>(undefined);
  const initialHeroAspectVarRef = useRef<string | null | undefined>(undefined);
  const shouldManageAspectRef = useRef<boolean | null>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const heroMedia = mediaOverride ?? hero.media;
  // `ratioOverride` is declared in the template definition (slots.hero[].props.ratio)
  // and allows each design to enforce a default aspect when the media does not
  // provide one explicitly.
  // logInfo('HeroBlock - ratioOverride (temp anulado): ', ratioOverride)
  const responsiveRatio = heroMedia?.ratio; // ?? ratioOverride;

  const resolvedAspectRatio = useMemo(
    () => resolveResponsiveRatio(responsiveRatio, isMobile),
    [responsiveRatio, isMobile],
  );

  useEffect(() => {
    const node = heroRef.current?.parentElement as HTMLElement | null;
    if (!node) {
      return;
    }

    if (initialAspectRef.current === undefined) {
      const currentAspect = node.style.getPropertyValue('aspect-ratio').trim();
      initialAspectRef.current = currentAspect || null;

      const currentHeroAspect = node.style.getPropertyValue('--catalogue-hero-aspect').trim();
      initialHeroAspectVarRef.current = currentHeroAspect || null;

      shouldManageAspectRef.current = currentAspect === '';
    }

    const shouldManage = shouldManageAspectRef.current ?? false;
    if (!shouldManage) {
      return undefined;
    }

    if (resolvedAspectRatio) {
      node.style.setProperty('aspect-ratio', resolvedAspectRatio);
      node.style.setProperty('--catalogue-hero-aspect', resolvedAspectRatio);
    } else {
      node.style.removeProperty('aspect-ratio');
      node.style.removeProperty('--catalogue-hero-aspect');
    }

    return () => {
      if (!shouldManage) {
        return;
      }

      if (initialAspectRef.current) {
        node.style.setProperty('aspect-ratio', initialAspectRef.current);
      } else {
        node.style.removeProperty('aspect-ratio');
      }

      if (initialHeroAspectVarRef.current) {
        node.style.setProperty('--catalogue-hero-aspect', initialHeroAspectVarRef.current);
      } else {
        node.style.removeProperty('--catalogue-hero-aspect');
      }
    };
  }, [resolvedAspectRatio]);

  const heroCta = ctaOverride ?? hero.cta;
  const ctaNode = useMemo(() => buildHeroCta(heroCta), [heroCta]);
  const mediaNode = useMemo(
    () => buildHeroMediaNode(heroMedia, resolvedAspectRatio, hero.title),
    [heroMedia, resolvedAspectRatio, hero.title],
  );

  const headerId = 'catalogue-hero-heading';
  const descriptionId = hero.description ? 'catalogue-hero-description' : undefined;

  return (
    <section
      ref={heroRef}
      className="catalogue-panel__hero catalogue-hero"
      aria-labelledby={headerId}
      aria-describedby={descriptionId}
    >
      <div className="catalogue-hero__overlay" aria-hidden />
      <div className="catalogue-hero__content">
        <div className="catalogue-hero__body">
          {hero.eyebrow && (
            <Text className={`${config.eyebrowClassName} catalogue-hero__eyebrow`}>
              {hero.eyebrow}
            </Text>
          )}
          {showTitle && (
            <Title level={2} className="catalogue-hero__title" id={headerId}>
              {hero.title}
            </Title>
          )}
          {hero.description && (
            <Paragraph
              className="catalogue-hero__description text-ellipsis-3"
              id={descriptionId}
              type="secondary"
            >
              {hero.description}
            </Paragraph>
          )}
          <Space orientation="vertical" size={16} className="catalogue-hero__actions" wrap>
            {ctaNode}
            <Space size={[8, 8]} wrap className="catalogue-hero__stats">
              {/*<Tag className={config.resultTagClassName}>*/}
              {/*  {hero.resultCount.toLocaleString()} {hero.resultLabel ?? 'resultados'}*/}
              {/*</Tag>*/}
              {status.badges?.map((badge) => (
                <Tag
                  key={badge.key}
                  className={`catalogue-panel__status catalogue-panel__status--${badge.tone ?? 'info'}`}
                >
                  {badge.label}
                </Tag>
              ))}
            </Space>
          </Space>
        </div>
        {mediaNode}
      </div>
    </section>
  );
};

export const ToolbarBlock: FC<PanelBlockProps> = ({ design, filters, sort }) => {
  const config = getPanelConfig(design);
  const hasFilters = Boolean(filters?.chips?.length);
  const hasSort = Boolean(sort?.options?.length);
  const drawerContext = useCategoriesMenuDrawer();
  const showDrawerToggle = drawerContext.isEnabled;

  const registerTrigger = useCallback(
    (node: HTMLButtonElement | null) => {
      drawerContext.registerTrigger(node);
    },
    [drawerContext],
  );

  if (!hasFilters && !hasSort && !showDrawerToggle) {
    return null;
  }

  const chips = renderToolbarChips(filters, config);
  const clearButton = filters?.onClearAll ? (
    <Button
      type="link"
      className={config.clearButtonClassName}
      onClick={filters.onClearAll}
      disabled={filters.isClearDisabled}
    >
      Limpiar todo
    </Button>
  ) : null;

  return (
    <div className={`${config.toolbarClassName} catalogue-panel__toolbar-surface`}>
      <div className="catalogue-toolbar__row">
        <div className="catalogue-toolbar__controls">
          {showDrawerToggle ? (
            <Button
              type="default"
              className="catalogue-toolbar__toggle"
              icon={<FilterOutlined />}
              onClick={drawerContext.openDrawer}
              aria-controls={drawerContext.drawerId}
              aria-expanded={drawerContext.isOpen}
              aria-haspopup="dialog"
              ref={registerTrigger}
            >
              Filtros
            </Button>
          ) : null}
          <div className="catalogue-toolbar__filters">
            {chips}
            {clearButton}
          </div>
        </div>
        <div className="catalogue-toolbar__actions">
          {renderSortSelect(sort, config.sortClassName, config.sortPlaceholder)}
        </div>
      </div>
    </div>
  );
};

export const PanelHeadingBlock: FC<PanelBlockProps & { className?: string }> = ({ hero, className }) => {
  const resultLabel = hero.resultLabel ?? 'productos encontrados';

  return (
    <div className={className ?? 'catalogue-panel-heading'}>
      <h2>{hero.title}</h2>
      <p>
        {hero.resultCount.toLocaleString('es-AR')} {resultLabel}
      </p>
    </div>
  );
};

export const GridBlock: FC<PanelBlockProps> = ({ items, layout, banners = [], cardComponent }) => {
  const [gutterX, gutterY] = toGutterPair(layout.gutter);

  const normalizeSpan = useCallback((span: number): number => {
    if (!Number.isFinite(span)) {
      return 24;
    }
    return Math.max(1, Math.min(24, Math.round(span)));
  }, []);

  const baseSpans = useMemo<SpansByBp>(() => {
    const size = layout.itemCardSize ?? 'normal';
    return columnsToSpans(SIZE_TO_COLUMNS[size] ?? SIZE_TO_COLUMNS.normal);
  }, [layout.itemCardSize]);

  const overrideSpans = useMemo<SpansByBp>(() => {
    const grid = layout.grid;
    if (!grid) {
      return {};
    }

    return (Object.entries(grid) as Array<[
      keyof SpansByBp,
      number | { span?: number }
    ]>).reduce<SpansByBp>((acc, [breakpoint, value]) => {
      if (typeof value === 'number') {
        acc[breakpoint] = normalizeSpan(value);
        return acc;
      }

      if (value && typeof value === 'object' && typeof value.span === 'number') {
        acc[breakpoint] = normalizeSpan(value.span);
      }

      return acc;
    }, {} as SpansByBp);
  }, [layout.grid, normalizeSpan]);

  const colSpans = useMemo<SpansByBp>(
    () => ({ ...baseSpans, ...overrideSpans }),
    [baseSpans, overrideSpans],
  );

  const responsiveClassName = useMemo(() =>
    (Object.entries(colSpans) as Array<[keyof SpansByBp, number | undefined]>)
      .filter(([, span]) => typeof span === 'number' && span > 0)
      .map(([breakpoint, span]) => `ant-col-${breakpoint}-${normalizeSpan(span as number)}`)
      .join(' '),
    [colSpans, normalizeSpan]);

  const columnClassName = useMemo(
    () =>
      ['catalogue-panel__col', responsiveClassName]
        .filter(Boolean)
        .join(' '),
    [responsiveClassName],
  );

  // Check if top banners should be hidden from grid (they render elsewhere when position is 'hero')
  // Reading from template context which is updated in real-time by the draft
  const template = useCatalogueTemplate();
  const hideTopBanners = template.topBannerPosition === 'hero';

  return (
    <Row className="catalogue-panel__grid" gutter={[gutterX, gutterY]} wrap>
      <BannerSlots
        banners={banners}
        hideTopBanners={hideTopBanners}
        items={items.map((item, index) => (
          <Col key={item.id ?? index} className={columnClassName} {...colSpans}>
            <BaseCatalogueCard component={cardComponent} item={item} index={index} />
          </Col>
        ))}
      />
    </Row>
  );
};

export const EmptyStateBlock: FC<PanelBlockProps> = ({ emptyState, design }) => {
  const config = getPanelConfig(design);

  if (!emptyState) {
    return (
      <div className="catalogue-panel__empty">
        <Empty
          description={
            <Space orientation="vertical" size={4} className="text-center">
              <Text strong>No encontramos resultados</Text>
              <Text type="secondary">{config.defaultEmptyDescription}</Text>
            </Space>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="catalogue-panel__empty">
      <Empty
        description={
          <Space orientation="vertical" size={4} className="text-center">
            <Text strong>{emptyState.title}</Text>
            {emptyState.description && <Text type="secondary">{emptyState.description}</Text>}
          </Space>
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      >
        {emptyState.onAction && emptyState.actionLabel && (
          <Button type="primary" onClick={emptyState.onAction}>
            {emptyState.actionLabel}
          </Button>
        )}
      </Empty>
    </div>
  );
};

export const PaginationBlock: FC<PanelBlockProps> = ({ pagination, design }) => {
  if (!pagination?.hasMore) {
    return null;
  }

  const config = getPanelConfig(design);

  return (
    <div className="catalogue-panel__load-more">
      <Button
        type={config.loadMoreButtonType}
        size="large"
        onClick={pagination.onLoadMore}
        loading={pagination.isLoading}
        className={config.loadMoreButtonClassName}
      >
        {pagination.label ?? 'Ver más'}
      </Button>
    </div>
  );
};
